import axios from "axios";
import { toast } from "sonner";
import { SALES_ENABLED } from "./appConfig";
import { generujPismo, czyKreatorAsync } from "./generacjaAsync";
import { poradaAuto, czyPoradaAsync, INTERWAL_PORADY_SZYBKIEJ_MS } from "./poradaAsync";
import * as postepPorady from "./postepPorady";
import { umowyAuto, czyUmowyAsync } from "./umowyAsync";
import { badaniaAuto, czyBadaniaAsync } from "./badaniaAsync";

// In Docker: empty → relative URL → nginx proxy /api/ → backend:8001
// In dev: set REACT_APP_BACKEND_URL=http://localhost:8001 for direct access
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Attach JWT token to every request + fix Content-Type for FormData uploads
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("les_lex_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // When sending FormData, delete the default Content-Type so the browser
  // auto-sets multipart/form-data WITH the correct boundary (Safari fix)
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  return config;
});

// Handle 401 — attempt token refresh, then redirect to login if that fails
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    // Handle 429 — rate limited
    if (err.response?.status === 429) {
      console.warn("[API] Rate limited — too many requests");
      return Promise.reject(err);
    }

    // Handle 402 — token limit exceeded (miesięczny limit AI wyczerpany)
    if (err.response?.status === 402) {
      if (SALES_ENABLED) {
        // Tryb sprzedaży: kieruj na stronę planu (poza ścieżkami bezpiecznymi).
        const safePaths = ["/suspended", "/settings", "/pricing", "/login"];
        if (!safePaths.includes(window.location.pathname)) {
          window.location.href = "/suspended";
        }
      } else {
        // Tryb testowy (SALES_ENABLED=false): NIE wyrzucaj adwokata ze środka pracy
        // na martwy checkout Stripe. Neutralny komunikat, user zostaje tam gdzie jest.
        // id: dedupuje toast przy wielu równoległych requestach kończących się 402.
        toast.error(
          "Wyczerpano bieżący limit zasobów AI konta testowego. Napisz do nas — zwiększymy pulę.",
          { id: "ai-token-limit", duration: 8000 }
        );
      }
      return Promise.reject(err);
    }

    const originalRequest = err.config;
    // 401 = expired/invalid token → try refresh
    // 403 = permission denied → do NOT redirect to login (user is authenticated but lacks permission)
    if (err.response?.status === 403) {
      const detail = err.response?.data?.detail || "";
      // must_change_password enforcement — redirect to settings
      if (detail === "Wymagana zmiana hasla") {
        const safePaths = ["/settings", "/login", "/logout"];
        if (!safePaths.includes(window.location.pathname)) {
          window.location.href = "/settings?force_password_change=1";
        }
      }
      return Promise.reject(err); // Let the calling component handle other permission errors
    }
    const isAuthError = err.response?.status === 401;
    if (
      isAuthError &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !originalRequest.url?.includes("/auth/login") &&
      !originalRequest.url?.includes("/auth/config")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("les_lex_refresh_token");
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          localStorage.setItem("les_lex_token", data.access_token);
          if (data.refresh_token) {
            localStorage.setItem("les_lex_refresh_token", data.refresh_token);
          }
          api.defaults.headers.Authorization = `Bearer ${data.access_token}`;
          processQueue(null, data.access_token);
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.removeItem("les_lex_token");
          localStorage.removeItem("les_lex_refresh_token");
          localStorage.removeItem("les_lex_user");
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // No refresh token — clear and redirect
      localStorage.removeItem("les_lex_token");
      localStorage.removeItem("les_lex_refresh_token");
      localStorage.removeItem("les_lex_user");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// Auth
export const registerUser = (data) => api.post("/auth/register", data);
export const loginUser = (data) => api.post("/auth/login", data);
export const getMe = () => api.get("/auth/me");
export const logoutUser = (refreshToken) => api.post("/auth/logout", { refresh_token: refreshToken });

// Email verification
export const verifyEmail = (token) => api.post("/auth/verify-email", { token });
export const resendVerification = (email) => api.post("/auth/resend-verification", { email });

// 2FA
export const setup2FA = () => api.post("/auth/2fa/setup");
export const enable2FA = (code) => api.post("/auth/2fa/enable", { code });
export const disable2FA = (code) => api.post("/auth/2fa/disable", { code });

// Password
export const changePassword = (data) => api.post("/auth/password/change", data);
export const getPasswordPolicy = () => api.get("/auth/password/policy");
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email });
export const resetPassword = (token, newPassword) => api.post("/auth/reset-password", { token, new_password: newPassword });
export const getAuthConfig = () => api.get("/auth/config");

// Language settings
export const getUserLanguage = () => api.get("/user/language");
export const updateUserLanguage = (data) => api.put("/user/language", data);

// OAuth — Google
export const getGoogleAuthUrl = () => api.get("/auth/google/url");
export const googleCallback = (code) => api.post("/auth/google/callback", { code });

// Organizations (Kancelarie)
export const createOrganization = (data) => api.post("/organizations", data);
export const getMyOrganization = () => api.get("/organizations/my");
export const inviteMember = (orgId, data) => api.post(`/organizations/${orgId}/invite`, data);
export const acceptInvitation = (token) => api.post(`/organizations/invitations/${encodeURIComponent(token)}/accept`);
export const removeMember = (orgId, memberId) => api.delete(`/organizations/${orgId}/members/${memberId}`);

// Admin / RBAC
export const listUsers = () => api.get("/admin/users");
export const changeRole = (data) => api.post("/admin/role", data);
export const setTokenLimit = (data) => api.post("/admin/tokens", data);
export const changePlan = (userId, newPlan) => api.post(`/admin/users/${userId}/plan`, { new_plan: newPlan });
export const suspendUser = (userId, reason = "Decyzja administratora") => api.post(`/admin/users/${userId}/suspend`, { reason });
export const unsuspendUser = (userId) => api.post(`/admin/users/${userId}/unsuspend`);
export const deactivateUser = (userId) => api.post(`/admin/users/${userId}/deactivate`);
export const reactivateUser = (userId) => api.post(`/admin/users/${userId}/reactivate`);
export const adminResetPassword = (userId) => api.post(`/admin/users/${userId}/reset-password`);
export const adminDeleteUser = (userId) => api.delete(`/admin/users/${userId}`);

// Admin Email Inbox
export const getAdminEmails = (params = {}) => api.get("/admin/emails", { params });
export const getAdminEmail = (messageId) => api.get(`/admin/emails/${encodeURIComponent(messageId)}`);
export const syncAdminEmails = (force = false) => api.post(`/admin/emails/sync?force=${force}`);
export const analyzeAdminEmail = (messageId) => api.post(`/admin/emails/${encodeURIComponent(messageId)}/analyze`);
export const draftEmailResponse = (messageId, data) => api.post(`/admin/emails/${encodeURIComponent(messageId)}/draft-response`, data);
export const getInboxStats = () => api.get("/admin/emails/stats");
export const batchAnalyzeEmails = (messageIds) => api.post("/admin/emails/batch-analyze", { message_ids: messageIds }, { timeout: 600000 });
export const caseAnalyzeEmails = (messageIds, caseHint = "") =>
    api.post("/admin/emails/case-analyze", { message_ids: messageIds, case_hint: caseHint }, { timeout: 600000 });
export const downloadEmailAttachment = (fileId) => api.get(`/admin/emails/attachments/${fileId}`, { responseType: "blob" });
export const importEmailToProject = (messageId, projectId, includeAttachments = true) =>
    api.post(`/admin/emails/${encodeURIComponent(messageId)}/import-to-project`, { project_id: projectId, include_attachments: includeAttachments });
export const sendEmailReply = (messageId, toEmail, subject, body) =>
    api.post(`/admin/emails/${encodeURIComponent(messageId)}/send-reply`, { to_email: toEmail, subject, body });
export const batchImportEmailsToProject = (messageIds, projectId, includeAttachments = true) =>
    api.post("/admin/emails/batch-import-to-project", { message_ids: messageIds, project_id: projectId, include_attachments: includeAttachments });

// Projects
export const getProjects = () => api.get("/projects");
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post("/projects", data);
export const updateProjectStatus = (projectId, status) => api.patch(`/projects/${projectId}`, { status });
export const updateProjectModule = (projectId, module) => api.patch(`/projects/${projectId}/module`, { module });
export const deleteProject = (id) => api.delete(`/projects/${id}`);

// Sources
export const getSources = (projectId) => api.get(`/projects/${projectId}/sources`);
export const getSourcesWithContent = (projectId) => api.get(`/projects/${projectId}/sources?include_content=true`);
export const getSource = (projectId, sourceId) => api.get(`/projects/${projectId}/sources/${sourceId}`);
export const createSource = (projectId, data) => api.post(`/projects/${projectId}/sources`, data);
export const deleteSource = (projectId, sourceId) => api.delete(`/projects/${projectId}/sources/${sourceId}`);
export const batchDeleteSources = (projectId, sourceIds) => api.post(`/projects/${projectId}/sources/batch-delete`, { source_ids: sourceIds });
// Krok 3 (audyt 02.09): ponowna ekstrakcja (OCR per strona) + re-indeksacja z zachowanego pliku
export const reprocessSource = (projectId, sourceId) => api.post(`/projects/${projectId}/sources/${sourceId}/reprocess`);

// File upload (single)
export const uploadFile = (projectId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/projects/${projectId}/upload`, formData, {
    // Let axios auto-set Content-Type with boundary (Safari compatibility)
  });
};

// File upload (batch — multiple files + text extraction)
export const uploadFilesBatch = (projectId, files) => {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  return api.post(`/projects/${projectId}/upload-batch`, formData, {
    // Let axios auto-set Content-Type with boundary (Safari compatibility)
  });
};

// Documents / Generate
export const generateDocument = (projectId, data) => api.post(`/projects/${projectId}/generate`, data, { timeout: 600000 });
export const getDocument = (projectId) => api.get(`/projects/${projectId}/document`);
// KREATOR-ASYNC (19.10.2026): za flaga serwera (`GET /flags` -> `kreator_async`) generacja idzie jako zadanie w tle
// z odpytywaniem (Cloudflare zrywa zadanie bez bajtow po 100 s = 524). `generateDocumentAuto` zwraca `{ data }` jak
// `generateDocument`, wiec obsluga wyniku w UI jest ta sama; flaga wylaczona/niedostepna = trasa klasyczna.
export const startGenerateAsync = (projectId, data) => api.post(`/projects/${projectId}/generate/async`, data, { timeout: 60000 });
export const getGenerateJob = (projectId, jobId) => api.get(`/projects/${projectId}/generate/jobs/${jobId}`, { timeout: 30000 });
export const generateDocumentAuto = async (projectId, data, opcje = {}) =>
  generujPismo({
    asyncWlaczone: await czyKreatorAsync(() => api.get("/flags", { timeout: 15000 })),
    start: () => startGenerateAsync(projectId, data),
    odczytaj: (jobId) => getGenerateJob(projectId, jobId),
    klasycznie: () => generateDocument(projectId, data),
    ...opcje,
  });
// ASYNC-2 (19.10.2026): iterate i auto-fix tym samym wzorcem (ta sama flaga, jeden "slot" na projekt, wspolny GET
// statusu `/projects/{id}/jobs/{job_id}`). `iterateDocumentAuto` / `autoFixDocumentAuto` zwracaja `{ data }` jak
// `iterateDocument` / `autoFixDocument`; flaga wylaczona/niedostepna = trasa klasyczna.
export const startIterateAsync = (projectId, data) => api.post(`/projects/${projectId}/iterate/async`, data, { timeout: 60000 });
export const startAutoFixAsync = (projectId) => api.post(`/projects/${projectId}/auto-fix/async`, undefined, { timeout: 60000 });
export const getKreatorJob = (projectId, jobId) => api.get(`/projects/${projectId}/jobs/${jobId}`, { timeout: 30000 });
export const iterateDocumentAuto = async (projectId, data, opcje = {}) =>
  generujPismo({
    rodzaj: "iterate",
    klucz: projectId,
    payload: data,
    asyncWlaczone: await czyKreatorAsync(() => api.get("/flags", { timeout: 15000 })),
    start: () => startIterateAsync(projectId, data),
    odczytaj: (jobId) => getKreatorJob(projectId, jobId),
    klasycznie: () => iterateDocument(projectId, data),
    ...opcje,
  });
export const autoFixDocumentAuto = async (projectId, opcje = {}) =>
  generujPismo({
    rodzaj: "auto_fix",
    asyncWlaczone: await czyKreatorAsync(() => api.get("/flags", { timeout: 15000 })),
    start: () => startAutoFixAsync(projectId),
    odczytaj: (jobId) => getKreatorJob(projectId, jobId),
    klasycznie: () => autoFixDocument(projectId),
    ...opcje,
  });

// Export DOCX
// C4: dwie niezalezne zgody eksportu pisma - `force` (cytowania/przeglad) i `force_jakosc` (kontrola jakosci).
// `force` NIE zdejmuje odmowy jakosciowej (WPIECIE_C4.md pkt 6).
export const zgodyEksportu = (force, forceJakosc) => {
  const q = [];
  if (force) q.push("force=true");
  if (forceJakosc) q.push("force_jakosc=true");
  return q.length ? `?${q.join("&")}` : "";
};
export const exportDocx = (projectId, force = false, forceJakosc = false) => api.get(`/projects/${projectId}/export/docx${zgodyEksportu(force, forceJakosc)}`, { responseType: "blob" });

// Export HTML
export const exportHtml = (projectId, force = false, forceJakosc = false) => api.get(`/projects/${projectId}/export/html${zgodyEksportu(force, forceJakosc)}`, { responseType: "blob" });

// Review
// timeout 600s = parytet z generateDocument i nginx proxy_read_timeout — review/auto-fix
// przechodza ten sam lancuch QG (fix-pass + re-check potrafi trwac 256-314s).
// ASYNC-5 (20.10.2026): recenzja, deep-research, raport DD i nauka stylu trwaja minuty, a Cloudflare zrywa zadanie bez
// bajtow po 100 s (524). Za flaga serwera (`GET /flags` -> `badania_async`) te cztery funkcje ida trasami `/async`
// (202 + job_id) z odpytywaniem `GET /badania/jobs/{id}`; ZACHOWALY NAZWY I KSZTALT WYNIKU (`{ data }` jak z axiosa), wiec
// strony (CreatorPage, CreatorView, DueDiligencePage, SettingsPage) dzialaja bez zmian, a flaga wylaczona albo trasa
// niedostepna (404) = trasa klasyczna (wywolania `...Klasycznie` bajt w bajt jak przed zmiana). Logika: lib/badaniaAsync.js.
const pobierzFlagiBadania = () => api.get("/flags", { timeout: 15000 });
export const getBadaniaJob = (jobId) => api.get(`/badania/jobs/${jobId}`, { timeout: 30000 });
const reviewDocumentKlasycznie = (projectId, data) => api.post(`/projects/${projectId}/review`, data || {}, { timeout: 600000 });
export const startReviewDocumentAsync = (projectId, data) => api.post(`/projects/${projectId}/review/async`, data || {}, { timeout: 60000 });
export const reviewDocument = async (projectId, data, opcje = {}) =>
  badaniaAuto({
    asyncWlaczone: await czyBadaniaAsync(pobierzFlagiBadania),
    start: () => startReviewDocumentAsync(projectId, data),
    odczytaj: getBadaniaJob,
    klasycznie: () => reviewDocumentKlasycznie(projectId, data),
    ...opcje,
  });
export const autoFixDocument = (projectId) => api.post(`/projects/${projectId}/auto-fix`, undefined, { timeout: 600000 });

// AI Models Status
export const getAiModels = () => api.get("/ai/models");

// AI Models Admin Config (owner-only)
export const getAdminAiModels = () => api.get("/admin/ai/models");
export const updateAdminAiModels = (data) => api.put("/admin/ai/models", data);

// AI Temperature Config (owner/admin-only)
export const getAdminAiTemperatures = () => api.get("/admin/ai/temperatures");
export const updateAdminAiTemperatures = (data) => api.put("/admin/ai/temperatures", data);
export const resetAdminAiTemperatures = () => api.post("/admin/ai/temperatures/reset");

// Comparative Analysis (Mistral)
export const comparativeAnalysis = (data) => api.post("/ai/comparative-analysis", data);

// Iterate (feedback-based document improvement)
// timeout 600s — patrz reviewDocument; brak timeoutu = przyczyna zgloszenia a77fd27f
// (dokument powstal w bazie, front pokazal blad).
export const iterateDocument = (projectId, data) => api.post(`/projects/${projectId}/iterate`, data, { timeout: 600000 });

// Document rating (user feedback for AI improvement)
export const rateDocument = (documentId, data) => api.post(`/documents/${documentId}/rate`, data);

// Versions
export const getVersions = (projectId) => api.get(`/projects/${projectId}/versions`);
export const getVersion = (projectId, versionId) => api.get(`/projects/${projectId}/versions/${versionId}`);
export const rollbackVersion = (projectId, versionId) => api.post(`/projects/${projectId}/versions/${versionId}/rollback`);

// Studio chat
export const sendStudioMessage = (projectId, data) => api.post(`/projects/${projectId}/studio/chat`, data);
export const getChatHistory = (projectId) => api.get(`/projects/${projectId}/studio/history`);
export const clearChatHistory = (projectId) => api.delete(`/projects/${projectId}/studio/history`);

// Comments
export const getComments = (projectId) => api.get(`/projects/${projectId}/comments`);
export const addComment = (projectId, data) => api.post(`/projects/${projectId}/comments`, data);
export const resolveComment = (projectId, commentId) => api.post(`/projects/${projectId}/comments/${commentId}/resolve`);

// Deadlines
export const getDeadlines = (completed = false) => api.get(`/deadlines?completed=${completed}`);
export const createDeadline = (data) => api.post("/deadlines", data);
export const completeDeadline = (id) => api.post(`/deadlines/${id}/complete`);
export const deleteDeadline = (id) => api.delete(`/deadlines/${id}`);
// Terminy — propozycje z dokumentów (krok 2 audytu 02.09.2026): bez projectId = wszystkie projekty
export const getDeadlineProposals = (projectId) =>
  projectId ? api.get(`/projects/${projectId}/terminy/propozycje`) : api.get("/terminy/propozycje");
export const acceptDeadlineProposal = (projectId, sourceId, idx, body = {}) =>
  api.post(`/projects/${projectId}/sources/${sourceId}/terminy/${idx}/akceptuj`, body);
export const rejectDeadlineProposal = (projectId, sourceId, idx) =>
  api.post(`/projects/${projectId}/sources/${sourceId}/terminy/${idx}/odrzuc`);

// Notifications
export const getNotifications = (unreadOnly = false) => api.get(`/notifications?unread_only=${unreadOnly}`);
export const getNotificationCount = () => api.get("/notifications/count");
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post("/notifications/read-all");
export const deleteNotification = (id) => api.delete(`/notifications/${id}`);

// Audit Log
export const getAuditLog = (params = {}) => api.get("/audit-log", { params });
export const getMyAuditLog = (limit = 30) => api.get(`/audit-log/my?limit=${limit}`);
export const getAuditStats = () => api.get("/audit-log/stats");

// Subscriptions / Plans
export const getPlans = () => api.get("/plans");
export const getUsage = () => api.get("/usage");
export const createCheckout = (data) => api.post("/checkout", data);
export const cancelPlan = () => api.post("/plan/cancel");

// Legal Search (SAOS + ISAP)
export const legalSearch = (data) => api.post("/legal/search", data);
export const getJudgment = (id) => api.get(`/legal/judgment/${id}`);
export const legalAnalyze = (data) => api.post("/legal/analyze", data);

// Deep Research (multi-step AI research)
const deepResearchKlasycznie = (data) => api.post("/legal/deep-research", data);
export const startDeepResearchAsync = (data) => api.post("/legal/deep-research/async", data, { timeout: 60000 });
export const deepResearch = async (data, opcje = {}) =>
  badaniaAuto({
    asyncWlaczone: await czyBadaniaAsync(pobierzFlagiBadania),
    start: () => startDeepResearchAsync(data),
    odczytaj: getBadaniaJob,
    klasycznie: () => deepResearchKlasycznie(data),
    ...opcje,
  });

// Shepard's Citation Check
export const shepardCheck = (data) => api.post("/legal/shepard-check", data);
export const shepardDocumentCheck = (data) => api.post("/legal/shepard-document", data);

// SAOS Mass Import (admin)
export const saosMassImport = (data) => api.post("/saos/mass-import", data);

// Institutional Memory — Style Learning
const learnWritingStyleKlasycznie = () => api.post("/ai/learn-style", {}, { timeout: 780000 }); // 13min — up to 25 docs analysis
export const startLearnWritingStyleAsync = () => api.post("/ai/learn-style/async", {}, { timeout: 60000 });
export const learnWritingStyle = async (opcje = {}) =>
  badaniaAuto({
    asyncWlaczone: await czyBadaniaAsync(pobierzFlagiBadania),
    start: () => startLearnWritingStyleAsync(),
    odczytaj: getBadaniaJob,
    klasycznie: () => learnWritingStyleKlasycznie(),
    ...opcje,
  });
export const getStyleProfile = () => api.get("/ai/style-profile");
export const deleteStyleProfile = () => api.delete("/ai/style-profile");
export const shareStyleWithOrg = () => api.post("/ai/style-profile/share-with-org");

// Style Samples — upload firm documents as style references
export const uploadStyleSample = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  // Do NOT set Content-Type manually — axios auto-detects FormData and sets
  // multipart/form-data WITH the correct boundary (Safari compatibility fix)
  return api.post("/ai/style-samples", formData);
};
export const listStyleSamples = () => api.get("/ai/style-samples");
export const deleteStyleSample = (sampleId) => api.delete(`/ai/style-samples/${sampleId}`);

// Knowledge Base (RAG)
// 2026-05-04: timeout 90s na list/stats — public KB ma 222k chunkow, cold-cache scan trwa ~47s.
// Pre-warm na startupie zalatwia 99% przypadkow, ale jesli cache wygasl + crawler zajety —
// pierwszy request moze trafic na cold scan. 90s daje bezpieczny margines.
export const knowledgeList = (params = {}) => api.get("/knowledge/list", { params, timeout: 90000 });
export const knowledgeStats = () => api.get("/knowledge/stats", { timeout: 90000 });
export const knowledgeAdd = (data) => api.post("/knowledge/add", data);
export const knowledgeSearch = (data) => api.post("/knowledge/search", data);
export const knowledgeDelete = (id) => api.delete(`/knowledge/${id}`);

// Knowledge Graph
export const buildKnowledgeGraph = () => api.post("/knowledge-graph/build");
export const getKGStats = () => api.get("/knowledge-graph/stats");
export const getRelatedArticles = (article, code, depth = 2) => api.get(`/knowledge-graph/related?article=${article}&code=${code}&depth=${depth}`);
export const searchKGNodes = (q) => api.get(`/knowledge-graph/search?q=${q}`);
export const getKGFullGraph = (maxNodes = 200) => api.get(`/knowledge-graph/full?max_nodes=${maxNodes}`);

// Legal Calculators
export const calcCourtFee = (data) => api.post("/calculators/court-fee", data);
export const calcInterest = (data) => api.post("/calculators/interest", data);
export const calcLimitation = (data) => api.post("/calculators/limitation", data);
export const getLimitationTypes = () => api.get("/calculators/limitation-types");
export const calcDeadline = (data) => api.post("/calculators/deadline", data);
export const calcAttorneyFee = (data) => api.post("/calculators/attorney-fee", data);
export const calcBailiffFee = (data) => api.post("/calculators/bailiff-fee", data);
export const calcRecoveryCompensation = (data) => api.post("/calculators/recovery-compensation", data);
export const calcNotaryFee = (data) => api.post("/calculators/notary-fee", data);
export const calcStampDuty = (data) => api.post("/calculators/stamp-duty", data);
export const calcValorization = (data) => api.post("/calculators/valorization", data);
export const getFixedFeeTypes = () => api.get("/calculators/fixed-fee-types");
export const getStampDutyTypes = () => api.get("/calculators/stamp-duty-types");
export const getNotaryActTypes = () => api.get("/calculators/notary-act-types");
export const getAttorneyCaseTypes = () => api.get("/calculators/attorney-case-types");

// PDF Lab
export const pdfMerge = (projectId, files) => {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  return api.post(`/projects/${projectId}/pdf/merge`, formData, {
    // Let axios auto-set Content-Type with boundary (Safari compatibility)
    responseType: "blob",
  });
};
export const pdfSplit = (projectId, file, pages) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/projects/${projectId}/pdf/split?pages=${encodeURIComponent(pages)}`, formData, {
    // Let axios auto-set Content-Type with boundary (Safari compatibility)
    responseType: "blob",
  });
};
export const pdfCompress = (projectId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/projects/${projectId}/pdf/compress`, formData, {
    // Let axios auto-set Content-Type with boundary (Safari compatibility)
    responseType: "blob",
  });
};
export const pdfRotate = (projectId, file, angle) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/projects/${projectId}/pdf/rotate?angle=${angle}`, formData, {
    // Let axios auto-set Content-Type with boundary (Safari compatibility)
    responseType: "blob",
  });
};

// OCR
export const ocrDocument = (projectId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/projects/${projectId}/ocr`, formData, {
    // Let axios auto-set Content-Type with boundary (Safari compatibility)
  });
};

// AI Advanced
export const benchmarkContract = (data) => api.post("/ai/benchmark", data);
export const suggestClauses = (data) => api.post("/ai/suggest-clauses", data);
export const categorizeDocument = (data) => api.post("/ai/categorize", data);
export const predictOutcome = (data) => api.post("/ai/predict", data);

// Case Prediction
export const predictCaseOutcome = (data) => api.post("/prediction/predict", data);

// Contract Analysis
// ASYNC-4 (20.10.2026): analiza umow trwa ok. 2-5 min, a Cloudflare zrywa zadanie bez bajtow po 100 s (524). Za flaga
// serwera (`GET /flags` -> `umowy_async`) te trzy funkcje ida trasami `/async` (202 + job_id) z odpytywaniem
// `GET /contracts/jobs/{id}`; ZACHOWALY NAZWY I KSZTALT WYNIKU (`{ data }` jak z axiosa), wiec strony analizy umow
// (ContractAnalysisPage, ContractView) dzialaja bez zmian, a flaga wylaczona albo trasa niedostepna (404) = trasa
// klasyczna (wywolania ponizej bajt w bajt jak przed zmiana). Logika: lib/umowyAsync.js.
const pobierzFlagiUmowy = () => api.get("/flags", { timeout: 15000 });
const analyzeContractKlasycznie = (data) => api.post("/contracts/analyze", data, { timeout: 600000 });
const analyzeContractFileKlasycznie = (formData) => api.post("/contracts/analyze-file", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
const analyzeContractBatchKlasycznie = (formData) => api.post("/contracts/analyze-batch", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
export const startAnalyzeContractAsync = (data) => api.post("/contracts/analyze/async", data, { timeout: 60000 });
export const startAnalyzeContractFileAsync = (formData) => api.post("/contracts/analyze-file/async", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
export const startAnalyzeContractBatchAsync = (formData) => api.post("/contracts/analyze-batch/async", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
export const getContractJob = (jobId) => api.get(`/contracts/jobs/${jobId}`, { timeout: 30000 });
export const analyzeContract = async (data, opcje = {}) =>
  umowyAuto({
    asyncWlaczone: await czyUmowyAsync(pobierzFlagiUmowy),
    start: () => startAnalyzeContractAsync(data),
    odczytaj: getContractJob,
    klasycznie: () => analyzeContractKlasycznie(data),
    ...opcje,
  });
export const analyzeContractFile = async (formData, opcje = {}) =>
  umowyAuto({
    asyncWlaczone: await czyUmowyAsync(pobierzFlagiUmowy),
    start: () => startAnalyzeContractFileAsync(formData),
    odczytaj: getContractJob,
    klasycznie: () => analyzeContractFileKlasycznie(formData),
    ...opcje,
  });
export const analyzeContractBatch = async (formData, opcje = {}) =>
  umowyAuto({
    asyncWlaczone: await czyUmowyAsync(pobierzFlagiUmowy),
    start: () => startAnalyzeContractBatchAsync(formData),
    odczytaj: getContractJob,
    klasycznie: () => analyzeContractBatchKlasycznie(formData),
    ...opcje,
  });
export const getContractHistory = (skip = 0, limit = 20) => api.get(`/contracts/history?skip=${skip}&limit=${limit}`);
export const getContractAnalysisDetail = (id) => api.get(`/contracts/history/${id}`);
export const deleteContractAnalysis = (id) => api.delete(`/contracts/history/${id}`);
export const compareContracts = (data) => api.post("/contracts/compare", data);

// AI Clarify — Pre-analysis chat
export const aiClarify = (data) => api.post("/ai/clarify", data, { timeout: 300000 });
export const contractChat = (data) => api.post("/contracts/chat", data, { timeout: 300000 });
export const exportContractPDF = (analysis) => api.post("/contracts/export-pdf", { analysis }, { responseType: "blob", timeout: 30000 });
// DOCX-01: Word obok PDF analizy umowy
export const exportContractDocx = (analysis) => api.post("/contracts/export-docx", { analysis }, { responseType: "blob", timeout: 30000 });

// SAOS Auto-Import
export const triggerSaosImport = (data) => api.post("/saos/import", data);
export const getSaosStatus = () => api.get("/saos/status");

// ELI — Nowelizacje z api.sejm.gov.pl
export const eliImport = (data) => api.post("/eli/import", data);
export const eliImportAct = (data) => api.post("/eli/import-act", data);
export const getEliStatus = () => api.get("/eli/status");

// EUR-Lex — Dyrektywy UE (SPARQL)
export const eurLexImport = (data) => api.post("/eurlex/import", data);

// CEJSH — Artykuly prawnicze (OAI-PMH)
export const cejshImport = (data) => api.post("/cejsh/import", data);

// CeON — Prace naukowe (OAI-PMH)
export const ceonImport = (data) => api.post("/ceon/import", data);

// OpenLEX — Komentarze prawne (scraping)
export const openLexImport = (data) => api.post("/openlex/import", data);

// Unified import status
export const getImportStatus = () => api.get("/import/status");

// CADR-011 Faza 1 — backfill reindex akt sprawy do case-RAG (owner/admin)
export const reindexCaseRag = () => api.post("/admin/case-rag/reindex");

// CADR-011 Faza 2 — backfill streszczeń akt sprawy (panorama, owner/admin)
export const generateCaseRagSummaries = () => api.post("/admin/case-rag/generate-summaries");

// CADR-011 Faza 4 — backfill osi czasu akt sprawy (timeline, owner/admin)
export const generateCaseRagTimelines = () => api.post("/admin/case-rag/generate-timelines");

// CADR-011 Faza 3 — pełna analiza przygotowawcza (synteza map-reduce)
// Gated by /user/synthesis-consent (Opcja D Lean Build, CADR-005 mitigation)
export const getSynthesisConsent = () => api.get("/user/synthesis-consent");
export const grantSynthesisConsent = (clauseVersion = "1.0") =>
  api.post("/user/synthesis-consent", { accepted: true, clause_version: clauseVersion });
export const revokeSynthesisConsent = () => api.delete("/user/synthesis-consent");
export const getSynthesisConsentModal = () => api.get("/user/synthesis-consent/modal");

export const startProjectSynthesis = (projectId, lang = "pl") =>
  api.post(`/projects/${projectId}/synthesis`, { lang });
export const listProjectSyntheses = (projectId, limit = 20) =>
  api.get(`/projects/${projectId}/syntheses?limit=${limit}`);
export const getSynthesisStatus = (synthesisId) =>
  api.get(`/synthesis/${synthesisId}`);
export const acceptSynthesis = (synthesisId) =>
  api.post(`/synthesis/${synthesisId}/accept`, { reviewed: true });

// ePUAP / Court Portal
export const getFilingTypes = () => api.get("/epuap/filing-types");
export const generateFiling = (data) => api.post("/epuap/generate-filing", data);
export const generateEpu = (data) => api.post("/epuap/generate-epu", data);
export const trackCase = (data) => api.post("/cases/track", data);
export const getTrackedCases = (status) => api.get(`/cases/tracked${status ? `?status=${status}` : ""}`);
export const updateCaseStatus = (caseId, data) => api.put(`/cases/${caseId}/status`, data);
export const addCaseNote = (caseId, data) => api.post(`/cases/${caseId}/notes`, data);
export const deleteTrackedCase = (caseId) => api.delete(`/cases/${caseId}`);
export const refreshCaseSaos = (caseId) => api.post(`/cases/${caseId}/refresh-saos`);
export const searchKrs = (query) => api.get(`/krs/search?query=${encodeURIComponent(query)}`);

// Legal Advice (Porada Prawna)
// PORADA-TLE (27.10.2026): porada SZYBKA tez przekracza 100 s za tunelem Cloudflare (zmierzone 27.10 07:43: > 2,5 min
// = RAG + watki Bielika na CPU + model -> 524 bez pierwszego bajtu, a backend liczy dalej). Za ta sama flaga serwera
// `porada_async` quickAdvice / quickAdviceWithFile ida trasami `/advice/quick/async` i `/advice/quick-with-file/async`
// (202 + job_id, odpytywanie `GET /advice/jobs/{id}` co 3 s); ZACHOWALY NAZWY I KSZTALT WYNIKU, wiec zamrozone strony
// porad (AdvicePage, AdviceView) dzialaja bez zmian. Postep (etap, czas od startu, swiezosc) trafia do sklepu
// `lib/postepPorady.js`, z ktorego rysuje go globalny `components/PostepPoradySzybkiej.jsx` (App.js). Tryb klasyczny
// (flaga off / trasa 404) = jak dotad, bez wskaznika.
const quickAdviceKlasycznie = (data) => api.post("/advice/quick", data, { timeout: 600000 });
export const startQuickAdviceAsync = (data) => api.post("/advice/quick/async", data, { timeout: 60000 });
export const startQuickAdviceWithFileAsync = (formData) => api.post("/advice/quick-with-file/async", formData, { timeout: 300000 });
export { INTERWAL_PORADY_SZYBKIEJ_MS };
/** Opcje `poradaAuto` wpinajace sklep postepu porady szybkiej (klasyk + cockpit). */
const zPostepemSzybkiej = () => ({
  interwalMs: INTERWAL_PORADY_SZYBKIEJ_MS,
  przyStarcie: (jobId) => postepPorady.rozpocznij({ rodzaj: "quick", jobId }),
  przyOdczycie: (res) => { postepPorady.odnotujStatus(res?.data); postepPorady.odnotujSprawdzenie(); },
  przyPostepie: (p) => postepPorady.odnotujPostep(p),
});
/** PORADA-TLE-2: to samo dla porady pogłębionej (deep, deep-with-file) - 8 etapów zapisuje backend, czas z zegara serwera. */
const zPostepemGlebokiej = () => ({
  przyStarcie: (jobId) => postepPorady.rozpocznij({ rodzaj: "deep", jobId }),
  przyOdczycie: (res) => { postepPorady.odnotujStatus(res?.data); postepPorady.odnotujSprawdzenie(); },
  przyPostepie: (p) => postepPorady.odnotujPostep(p),
});
/** Koniec zadania do sklepu postepu (bez trwajacego zadania w sklepie = nic; tryb klasyczny). */
const zakonczPostepSzybkiej = (obietnica) => obietnica.then(
  (r) => { postepPorady.zakoncz("gotowe"); return r; },
  (e) => { postepPorady.zakoncz("blad"); throw e; },
);
export const quickAdvice = async (data, opcje = {}) =>
  zakonczPostepSzybkiej(poradaAuto({
    asyncWlaczone: await czyPoradaAsync(pobierzFlagiPorady),
    start: () => startQuickAdviceAsync(data),
    odczytaj: getAdviceJob,
    klasycznie: () => quickAdviceKlasycznie(data),
    ...zPostepemSzybkiej(),
    ...opcje,
  }));
// axios < backend = 499 po 900 s, zgłoszenie luki 02.09 — tu dodatkowo: 180 s < sam
// zmierzony RAG 114 s + LLM ~60-120 s (wydajnosc.md §5.2). Backend NIE ma jednej stalej
// budzetu dla quick-with-file; 300 s >= zmierzony backend + 30 s zapasu i <= nginx
// 1200 s dla /api/(advice|contracts)/ (nginx/nginx.conf:276-277); rekomendacja §6 pkt 6.
const quickAdviceWithFileKlasycznie = (formData) => api.post("/advice/quick-with-file", formData, { timeout: 300000 });
export const quickAdviceWithFile = async (formData, opcje = {}) =>
  zakonczPostepSzybkiej(poradaAuto({
    asyncWlaczone: await czyPoradaAsync(pobierzFlagiPorady),
    start: () => startQuickAdviceWithFileAsync(formData),
    odczytaj: getAdviceJob,
    klasycznie: () => quickAdviceWithFileKlasycznie(formData),
    ...zPostepemSzybkiej(),
    ...opcje,
  }));
// axios < backend = 499 po 900 s, zgłoszenie luki 02.09 — axios odcinal deep ~15 s
// PRZED koncem backendu (zmierzony pipeline ~915 s, 12/13 wywolan = 499). 1150 s
// <= nginx 1200 s (nginx/nginx.conf:276-277), z zapasem 50 s.
const deepAdviceKlasycznie = (data) => api.post("/advice/deep", data, { timeout: 1150000 });
// ASYNC-3 (19.10.2026): porady poglebione (deep, deep-with-file, tax-optimization, tax-prediction) trwaja 400-540 s, a
// Cloudflare zrywa zadanie bez bajtow po 100 s (524). Za flaga serwera (`GET /flags` -> `porada_async`) te cztery
// funkcje ida trasami `/async` (202 + job_id) z odpytywaniem `GET /advice/jobs/{id}`; ZACHOWALY NAZWY I KSZTALT WYNIKU
// (`{ data }` jak z axiosa), wiec strony porad (AdvicePage, AdviceView) dzialaja bez zmian, a flaga wylaczona albo trasa
// niedostepna (404) = trasa klasyczna. Logika: lib/poradaAsync.js.
const pobierzFlagiPorady = () => api.get("/flags", { timeout: 15000 });
export const startDeepAdviceAsync = (data) => api.post("/advice/deep/async", data, { timeout: 60000 });
export const startDeepAdviceWithFileAsync = (formData) => api.post("/advice/deep-with-file/async", formData, { timeout: 600000 });
export const startTaxOptimizationAsync = (data) => api.post("/advice/tax-optimization/async", data, { timeout: 60000 });
export const startTaxPredictionAsync = (data) => api.post("/advice/tax-prediction/async", data, { timeout: 60000 });
export const getAdviceJob = (jobId) => api.get(`/advice/jobs/${jobId}`, { timeout: 30000 });
export const deepAdvice = async (data, opcje = {}) =>
  zakonczPostepSzybkiej(poradaAuto({
    asyncWlaczone: await czyPoradaAsync(pobierzFlagiPorady),
    start: () => startDeepAdviceAsync(data),
    odczytaj: getAdviceJob,
    klasycznie: () => deepAdviceKlasycznie(data),
    ...zPostepemGlebokiej(),
    ...opcje,
  }));
// axios < backend = 499 po 900 s, zgłoszenie luki 02.09 — jak wyzej (deep-with-file).
const deepAdviceWithFileKlasycznie = (formData) => api.post("/advice/deep-with-file", formData, { timeout: 1150000 });
export const deepAdviceWithFile = async (formData, opcje = {}) =>
  zakonczPostepSzybkiej(poradaAuto({
    asyncWlaczone: await czyPoradaAsync(pobierzFlagiPorady),
    start: () => startDeepAdviceWithFileAsync(formData),
    odczytaj: getAdviceJob,
    klasycznie: () => deepAdviceWithFileKlasycznie(formData),
    ...zPostepemGlebokiej(),
    ...opcje,
  }));
export const getAdviceHistory = (params = {}) => api.get("/advice/history", { params });

// --- Cross-module history ---
export const getSearchHistory = (skip = 0, limit = 30) => api.get(`/legal/search/history?skip=${skip}&limit=${limit}`);
export const getPredictionHistory = (skip = 0, limit = 30) => api.get(`/prediction/history?skip=${skip}&limit=${limit}`);
export const getGlobalHistory = (skip = 0, limit = 50, days = 30) => api.get(`/history/global?skip=${skip}&limit=${limit}&days=${days}`);
export const getAdviceDetail = (id) => api.get(`/advice/${id}`);
export const rateAdvice = (id, data) => api.post(`/advice/${id}/rate`, data);
export const exportAdvicePDF = (id) => api.post(`/advice/${id}/export-pdf`, {}, { responseType: "blob", timeout: 30000 });
// DOCX-01: Word obok PDF (ta sama zapisana tresc porady)
export const exportAdviceDocx = (id) => api.post(`/advice/${id}/export-docx`, {}, { responseType: "blob", timeout: 30000 });
export const adviceChat = (data) => api.post("/advice/chat", data, { timeout: 600000 });
// axios < backend = 499 po 900 s, zgłoszenie luki 02.09 — pipeline tax wieloetapowy
// jak deep (wydajnosc.md §5.2: Borg 180 s + Claude); 1150 s <= nginx 1200 s.
const taxOptimizationKlasycznie = (data) => api.post("/advice/tax-optimization", data, { timeout: 1150000 });
export const taxOptimization = async (data, opcje = {}) =>
  poradaAuto({
    asyncWlaczone: await czyPoradaAsync(pobierzFlagiPorady),
    start: () => startTaxOptimizationAsync(data),
    odczytaj: getAdviceJob,
    klasycznie: () => taxOptimizationKlasycznie(data),
    ...opcje,
  });
// axios < backend = 499 po 900 s, zgłoszenie luki 02.09 — jak wyzej (tax-prediction).
const taxPredictionKlasycznie = (data) => api.post("/advice/tax-prediction", data, { timeout: 1150000 });
export const taxPrediction = async (data, opcje = {}) =>
  poradaAuto({
    asyncWlaczone: await czyPoradaAsync(pobierzFlagiPorady),
    start: () => startTaxPredictionAsync(data),
    odczytaj: getAdviceJob,
    klasycznie: () => taxPredictionKlasycznie(data),
    ...opcje,
  });
export const calculatePIT = (data) => api.post("/advice/calculate-pit", data);
export const calculateCIT = (data) => api.post("/advice/calculate-cit", data);
export const calculateVAT = (data) => api.post("/advice/calculate-vat", data);
export const calculateKKS = (data) => api.post("/advice/calculate-kks", data);

// --- User Reports (zgloszenia) ---
export const submitReport = (data) => api.post("/reports/submit", data);
export const getMyReports = (limit = 50) => api.get(`/reports/my?limit=${limit}`);
export const getMyReportDetail = (id) => api.get(`/reports/my/${id}`);
export const addMyReportComment = (id, text) => api.post(`/reports/my/${id}/comment`, { text });
export const confirmMyReportFix = (id) => api.post(`/reports/my/${id}/confirm`);
export const rejectMyReportFix = (id, reason) => api.post(`/reports/my/${id}/reject-fix`, { reason });
// Zalaczniki do zgloszen — jedno wywolanie na plik (multipart, pole "file").
// Nie ustawiamy Content-Type recznie: interceptor wykryje FormData i ustawi
// multipart/form-data z poprawnym boundary (Safari fix).
export const attachReportFile = (reportId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/reports/${reportId}/attach`, fd, { timeout: 120000 });
};
// Pobieranie zalacznika jako blob (endpoint wymaga JWT — interceptor dokleja token).
export const downloadReportAttachment = (reportId, fileId) =>
  api.get(`/reports/${reportId}/attachment/${fileId}`, { responseType: "blob" });
// Admin
export const adminListReports = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return api.get(`/admin/reports${query ? `?${query}` : ""}`);
};
export const adminReportDetail = (id) => api.get(`/admin/reports/${id}`);
export const adminUpdateReport = (id, data) => api.patch(`/admin/reports/${id}`, data);
export const adminDeleteReport = (id) => api.delete(`/admin/reports/${id}`);
export const adminBulkDeleteReports = (data) => api.post("/admin/reports/bulk-delete", data);
export const adminReportsStats = () => api.get("/admin/reports/stats/summary");

// Feature flags (per-user)
export const getMyFeatureFlags = () => api.get("/me/features");

// AI Task cancel (Faza 1.3 UX 2026 — anuluj dlugotrwala operacje AI)
export const cancelAITask = (taskId) => api.post(`/ai/cancel/${taskId}`);
export const listActiveAITasks = () => api.get("/ai/active");


// Voice Interface
export const processVoice = (data) => api.post("/voice/process", data);
export const getVoiceSettings = () => api.get("/voice/settings");

// Document Templates
export const getDocTemplates = (category) => api.get(`/templates${category ? `?category=${category}` : ""}`);
export const getTemplateCategories = () => api.get("/templates/categories");
export const getDocTemplate = (id) => api.get(`/templates/${id}`);
export const renderDocTemplate = (data) => api.post("/templates/render", data);

// EU AI Act Compliance
export const getComplianceOverview = () => api.get("/compliance/overview");
export const getComplianceFeatures = (feature) => api.get(`/compliance/features${feature ? `?feature=${feature}` : ""}`);
export const getAiDecisionsLog = (feature, limit, offset) => api.get(`/compliance/ai-decisions?feature=${feature || ""}&limit=${limit || 50}&offset=${offset || 0}`);
export const reviewAiDecision = (logId, data) => api.post(`/compliance/ai-decisions/${logId}/review`, data);
export const getDataGovernance = () => api.get("/compliance/data-governance");
export const getBiasReport = (days) => api.get(`/compliance/bias-report?days=${days || 30}`);
export const getFullComplianceReport = () => api.get("/compliance/full-report");

// Billing & Plans
export const getBillingPlans = () => api.get("/billing/plans");
export const getBillingUsage = () => api.get("/billing/usage");
export const subscribeToPlan = (planId, period) => api.post(`/billing/subscribe?plan_id=${planId}&billing_period=${period || "monthly"}`);
export const cancelSubscription = () => api.post("/billing/cancel");
export const getInvoices = () => api.get("/billing/invoices");
export const checkFeatureAccess = (feature) => api.post("/billing/check-feature", { feature });

// Developer API Keys
export const getApiDocs = () => api.get("/developer/api-docs");
export const createApiKey = (data) => api.post("/developer/api-keys", data);
export const listApiKeys = () => api.get("/developer/api-keys");
export const revokeApiKey = (keyId) => api.delete(`/developer/api-keys/${keyId}`);

// Negotiation
export const createNegotiation = (data) => api.post("/negotiations", data);
export const createNegotiationFromFiles = (formData) => api.post("/negotiations/create-from-files", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
export const listNegotiations = () => api.get("/negotiations");
export const getNegotiation = (id) => api.get(`/negotiations/${id}`);
export const analyzeNegotiation = (id) => api.post(`/negotiations/${id}/analyze`);
export const generateCounterproposal = (id, data) => api.post(`/negotiations/${id}/counterproposal`, data);
export const settleClause = (id, data) => api.post(`/negotiations/${id}/settle`, data);

// PDF Export
export const exportPdf = (projectId, force = false, forceJakosc = false) => api.get(`/projects/${projectId}/export/pdf${zgodyEksportu(force, forceJakosc)}`, { responseType: "blob" });

// Agent Workflows
export const getAgentTemplates = () => api.get("/agent/templates");
export const startAgentRun = (projectId, data) => api.post(`/projects/${projectId}/agent/run`, data);
export const getAgentStatus = (agentId) => api.get(`/agent/${agentId}/status`);
export const getAgentRuns = (projectId) => api.get(`/projects/${projectId}/agent/runs`);

// Analytics
export const getDashboardAnalytics = () => api.get("/analytics/dashboard");
export const getDetailedAnalytics = (days = 30) => api.get(`/analytics/detailed?days=${days}`);

// Global Search
export const globalSearch = (query) => api.post("/search", { query });

// GDPR / RODO
export const exportUserData = () => api.get("/user/export");
export const deleteUserAccount = () => api.post("/user/delete");
export const getPrivacyPolicy = () => api.get("/privacy");

// Contact
export const submitContact = (data) => api.post("/contact", data);

// Feature Flags
export const getFeatureFlags = () => api.get("/flags");
export const getAdminFlags = () => api.get("/admin/flags");
export const updateFeatureFlag = (name, data) => api.put(`/admin/flags/${name}`, data);
export const createFeatureFlag = (data) => api.post("/admin/flags", data);

// Trash / Recycle Bin
export const getTrashItems = () => api.get("/trash");
export const getTrashStats = () => api.get("/trash/stats");
export const restoreTrashItem = (type, id) => api.post(`/trash/${type}/${id}/restore`);
export const permanentDeleteTrashItem = (type, id) => api.delete(`/trash/${type}/${id}`);
export const emptyTrash = () => api.post("/trash/empty");

// Tracing
export const getTraces = (limit = 50) => api.get(`/internal/traces?limit=${limit}`);
export const getTraceStats = () => api.get("/internal/trace-stats");

// Health check
export const healthCheck = () => api.get("/");

// ==================== Per-User Email ====================
export const saveEmailConfig = (data) => api.post("/email/config", data);
export const getEmailConfig = () => api.get("/email/config");
export const deleteEmailConfig = () => api.delete("/email/config");
export const testEmailConfig = (data) => api.post("/email/config/test", data);
// OAuth2 email
export const getOAuthEmailUrl = (provider) => api.get(`/email/oauth/${provider}/url`);
export const oauthEmailCallback = (provider, code, state = "") =>
  api.post(`/email/oauth/${provider}/callback`, { code, state });
export const disconnectOAuthEmail = () => api.delete("/email/oauth/disconnect");
export const getUserEmailStats = () => api.get("/email/stats");
export const getUserEmails = (params = {}) => api.get("/email/list", { params });
export const getUserEmail = (messageId) => api.get(`/email/${encodeURIComponent(messageId)}`);
export const syncUserEmails = () => api.post("/email/sync");
export const analyzeUserEmail = (messageId) => api.post(`/email/${encodeURIComponent(messageId)}/analyze`);
export const importUserEmailToProject = (messageId, projectId, includeAttachments = true) =>
  api.post(`/email/${encodeURIComponent(messageId)}/import-to-project`, { project_id: projectId, include_attachments: includeAttachments });
export const deleteUserEmail = (messageId) => api.delete(`/email/${encodeURIComponent(messageId)}`);
export const batchDeleteUserEmails = (messageIds) => api.post("/email/batch-delete", { message_ids: messageIds });
// Per-user email — compose, reply, draft, attachments, batch
export const draftUserEmailResponse = (messageId, data = {}) =>
  api.post(`/email/${encodeURIComponent(messageId)}/draft-response`, data);
export const sendUserEmailReply = (messageId, toEmail, subject, body, cc = []) =>
  api.post(`/email/${encodeURIComponent(messageId)}/send-reply`, { to_email: toEmail, subject, body, cc });
export const sendUserEmail = (data) => api.post("/email/send", data);
export const downloadUserEmailAttachment = (fileId) =>
  api.get(`/email/attachments/${fileId}`, { responseType: "blob" });
export const batchAnalyzeUserEmails = (messageIds) =>
  api.post("/email/batch-analyze", { message_ids: messageIds }, { timeout: 600000 });
export const toggleUserEmailRead = (messageId, isRead) =>
  api.patch(`/email/${encodeURIComponent(messageId)}/read`, { is_read: isRead });
// Admin email delete
export const deleteAdminEmail = (messageId) => api.delete(`/admin/emails/${encodeURIComponent(messageId)}`);
export const batchDeleteAdminEmails = (messageIds) => api.post("/admin/emails/batch-delete", { message_ids: messageIds });

// ==================== WebSocket ====================

/**
 * Create a WebSocket connection for real-time notifications.
 * Returns a WebSocket instance. Call .close() to disconnect.
 *
 * Usage:
 *   const ws = createWebSocket((event, data) => {
 *     if (event === 'notification') showToast(data.title);
 *   });
 */
export function createWebSocket(onMessage) {
  const token = localStorage.getItem("les_lex_token");
  if (!token) return null;

  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const wsHost = BACKEND_URL ? new URL(BACKEND_URL).host : window.location.host;
  const ws = new WebSocket(`${wsProtocol}//${wsHost}/ws`);

  let authenticated = false;
  let retryCount = 0;
  const MAX_RETRIES = 5;
  let pingInterval = null;
  let reconnectTimeout = null;
  let _destroyed = false;

  ws.onopen = () => {
    ws.send(JSON.stringify({ token }));
    retryCount = 0; // Reset on successful connection
  };

  ws.onmessage = (evt) => {
    try {
      const { event, data } = JSON.parse(evt.data);
      if (event === "authenticated") {
        authenticated = true;
        return;
      }
      if (event === "error" && !authenticated) {
        console.error("WebSocket auth failed:", data?.message);
        ws.close();
        return;
      }
      if (onMessage) onMessage(event, data);
    } catch {
      // ignore malformed messages
    }
  };

  ws.onclose = () => {
    authenticated = false;
    if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
    if (_destroyed) return;
    // Auto-reconnect with limit
    if (retryCount < MAX_RETRIES && localStorage.getItem("les_lex_token")) {
      retryCount++;
      reconnectTimeout = setTimeout(() => {
        if (!_destroyed) {
          const newWs = createWebSocket(onMessage);
          // Update global ref so AIProgressBar and other components get the new WS
          window.__leslex_ws = newWs;
        }
      }, 5000 * retryCount); // Exponential backoff
    }
  };

  ws.onerror = () => {
    // Cleanup on error — onclose will also fire
    if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
  };

  // Keep-alive ping every 30s
  pingInterval = setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send("ping");
    } else {
      clearInterval(pingInterval);
      pingInterval = null;
    }
  }, 30000);

  // Return object with cleanup function for proper teardown
  ws._cleanup = () => {
    _destroyed = true;
    if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
    if (reconnectTimeout) { clearTimeout(reconnectTimeout); reconnectTimeout = null; }
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close();
    }
  };

  return ws;
}

// ==================== DUE DILIGENCE ====================

// DD CRUD
export const getDDProjects = () => api.get("/dd");
export const getDDProject = (ddId) => api.get(`/dd/${ddId}`);
export const createDDProject = (data) => api.post("/dd", data);
export const updateDDProject = (ddId, data) => api.patch(`/dd/${ddId}`, data);
export const deleteDDProject = (ddId) => api.delete(`/dd/${ddId}`);
export const getDDAreas = () => api.get("/dd/areas");

// DD Documents
export const uploadDDFiles = (ddId, files) => {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  return api.post(`/dd/${ddId}/upload`, formData, {
    // Let axios auto-set Content-Type with boundary (Safari compatibility)
    timeout: 300000,
  });
};
export const changeDDDocArea = (ddId, docId, area) =>
  api.patch(`/dd/${ddId}/documents/${docId}/area`, { area });
export const deleteDDDoc = (ddId, docId) =>
  api.delete(`/dd/${ddId}/documents/${docId}`);

// DD Analysis
export const startDDAnalysis = (ddId) => api.post(`/dd/${ddId}/analyze`);
export const getDDStatus = (ddId) => api.get(`/dd/${ddId}/status`);

// DD Report
const generateDDReportKlasycznie = (ddId) =>
  api.post(`/dd/${ddId}/report`, {}, { timeout: 600000 });
export const startGenerateDDReportAsync = (ddId) => api.post(`/dd/${ddId}/report/async`, {}, { timeout: 60000 });
export const generateDDReport = async (ddId, opcje = {}) =>
  badaniaAuto({
    asyncWlaczone: await czyBadaniaAsync(pobierzFlagiBadania),
    start: () => startGenerateDDReportAsync(ddId),
    odczytaj: getBadaniaJob,
    klasycznie: () => generateDDReportKlasycznie(ddId),
    ...opcje,
  });

export default api;

// PODSTAWA PRAWNA / provenance (2026-08-02) — slad zrodlowy dla adwokata.
// Lista zrodel = same metadane; pelna tresc przepisu + komentarze + orzecznictwo
// dociagane osobno po chunk_id, zeby nie ladowac megabajtow do przegladarki.
export const getKnowledgeSource = (params) => api.get("/knowledge/source", { params });
// Urzędowe brzmienie z Dziennika Ustaw + werdykt zgodności z tym, czego użył
// system. Wołane OSOBNO i dopiero po rozwinięciu pozycji — przy pierwszym
// użyciu aktu backend pobiera i rozbiera PDF z Dz.U. (kilka sekund).
export const getOfficialSource = (params) => api.get("/knowledge/official", { params });
export const getDocumentSources = (projectId) => api.get(`/projects/${projectId}/document/sources`);
// POWOŁANIA W PIŚMIE (05.10.2026) — skąd pochodzi każde powołanie (z bazy LES-LEX / do weryfikacji).
// Tylko dla znaczników w podglądzie Kreatora; `config` niesie `signal` (przerwanie nieaktualnego zapytania).
export const getDocumentPowolania = (projectId, config) => api.get(`/projects/${projectId}/document/powolania`, config);
